import React from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { useSEO } from "@/lib/seo";
import { ArrowRight, Eye, ShieldCheck, Zap } from "lucide-react";

const VALUES = [
  { Icon: Eye, title: "Clarity over clutter", body: "We show you what money is coming and what hasn't arrived — nothing more. No bookkeeping, no jargon, no dashboards you'll never open." },
  { Icon: ShieldCheck, title: "Your data is yours", body: "Every record is scoped to you and never shared across accounts. SMS is optional, transactional only, and off by default." },
  { Icon: Zap, title: "Fast and focused", body: "A freelancer should understand PaySignal in under a minute and log a record in fifteen seconds. Speed is a feature." },
];

export default function About() {
  useSEO({
    title: "About PaySignal — A ledger built for independent workers",
    description: "PaySignal is a lightweight invoice and payout tracker for freelancers, contractors, and gig workers. Learn what we build, why, and what we deliberately don't do.",
    canonical: window.location.origin + "/about",
  });

  return (
    <div className="min-h-screen bg-cream text-espresso">
      <SiteHeader />

      <section className="max-w-3xl mx-auto px-6 pt-16 pb-10">
        <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.15em] uppercase text-brandblue bg-brandblue-light rounded-full px-3 py-1.5">
          <span className="w-2 h-2 rounded-full bg-lime" /> About
        </span>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight mt-5 text-midnight leading-[1.05]" data-testid="about-title">
          A simple ledger for people who work for themselves.
        </h1>
        <p className="text-lg text-ink-secondary mt-5 leading-relaxed">
          Independent work is unpredictable enough. Getting paid shouldn't be a guessing game. PaySignal was built to answer three questions fast: what money am I expecting, what's becoming overdue, and what payouts should have already arrived?
        </p>
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-6">
        <div className="bg-white border rounded-3xl p-8">
          <h2 className="font-heading text-2xl font-extrabold tracking-tight text-midnight">Why we built it</h2>
          <p className="text-ink-secondary mt-3 leading-relaxed">
            Freelancers, contractors, and gig workers usually track money across scattered spreadsheets, notes apps, and memory. Invoices slip past due unnoticed; platform payouts arrive late without anyone realizing. Full accounting software is overkill and intimidating.
          </p>
          <p className="text-ink-secondary mt-3 leading-relaxed">
            PaySignal is intentionally small: one clean ledger for invoices and expected payouts, clear status at a glance, and optional text alerts when a date or payment state changes. That's it — and that's the point.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-3 gap-6">
          {VALUES.map(({ Icon, title, body }) => (
            <div key={title} className="border rounded-2xl bg-white p-6">
              <span className="inline-grid place-items-center w-11 h-11 rounded-xl bg-brandblue-light text-brandblue"><Icon size={20} /></span>
              <h3 className="font-heading text-lg font-bold mt-4 text-midnight">{title}</h3>
              <p className="text-sm text-ink-secondary mt-2 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-14">
        <div className="border rounded-3xl bg-white p-8">
          <h2 className="font-heading text-2xl font-extrabold tracking-tight text-midnight">What PaySignal is not</h2>
          <p className="text-ink-secondary mt-3 leading-relaxed">
            We think being clear about our limits builds trust. PaySignal does not process payments, hold or move money, contact your clients, or perform any kind of debt collection. It's a record-keeping and reminder tool — you stay in control, and you mark records paid yourself.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="bg-midnight rounded-3xl p-10 sm:p-14 text-center relative overflow-hidden">
          <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-brandblue/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-12 w-56 h-56 rounded-full bg-lime/10 blur-3xl pointer-events-none" />
          <h2 className="relative font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-white">Track what you're owed.</h2>
          <p className="relative text-white/70 mt-3 max-w-md mx-auto">Set up your ledger in under a minute — free, no card required.</p>
          <Link to="/signup"><Button size="lg" className="relative mt-7 bg-lime text-midnight hover:bg-lime-dark" data-testid="about-cta">Start tracking <ArrowRight size={18} className="ml-1" /></Button></Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
